
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, IconButton, useMediaQuery, Switch } from '@mui/material'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import PropTypes from 'prop-types'

import { PrimaryButton } from '../../default'

/**
 * @description Renders a data table component with customizable actions and styling.
 * 
 * @author Maria Eduarda Franke
 * @param {Object} props - The props object containing the following properties:
 *   - resource: The resource name for the table.
 *   - gender: The gender of the resource name.
 *   - tableType: The type of the table.
 *   - columns: The columns configuration for the table.
 *   - data: The data to be displayed in the table.
 *   - onCreate: The function to be called when creating a new item.
 *   - onUpdate: The function to be called when updating an item.
 *   - onDelete: The function to be called when deleting an item.
 *   - isLoading: A boolean indicating if the table is currently loading data.
 *   - isSaving: A boolean indicating if the table is currently saving data.
 *   - showAlertBanner: A boolean indicating if the table should show an alert banner.
 * @returns {JSX.Element} - The rendered DataTable component.
 */
const DataTable = ({
    resource, gender, tableType,
    columns, data,
    onCreate, onUpdate, onDelete, onSwitch,
    isLoading, isSaving, showAlertBanner, showButton = true
}) => {
    const isMobile = useMediaQuery('(max-width:600px)')

    const renderRowActions = ({ row }) => {
        return tableType === 'switch' ? (
            <Box display='flex' flexDirection='row'>
                <Switch id='tableSwitch' checked={row.original.is_active} onChange={() => onSwitch(row)} />
            </Box>
        ) : tableType === 'editAndSwitch' ? (
            <Box display='flex' flexDirection='row'>
                <Switch id='tableSwitch' checked={row.original.is_active} onChange={() => onSwitch(row)} />
                <IconButton onClick={() => onUpdate(row)}>
                    <EditIcon color='primary' />
                </IconButton>
            </Box>
        ) : (
            <Box display='flex' flexDirection='row'>
                <IconButton onClick={() => onUpdate(row)}>
                    <EditIcon color='primary' />
                </IconButton>
                <IconButton onClick={() => onDelete(row)}>
                    <DeleteIcon color='primary' />
                </IconButton>
            </Box>
        )
    }

    const renderTopToolbarCustomActions = () => {
        if (!showButton) {
            return
        }
        return gender === 'fem' ? (
            <PrimaryButton
                label={isMobile ? 'Nova' : `Nova ${resource}`}
                Icon={AddIcon}
                onClick={() => onCreate()}
            />
        ) : (
            <PrimaryButton
                label={isMobile ? 'Novo' : `Novo ${resource}`}
                Icon={AddIcon}
                onClick={() => onCreate()}
            />
        )
    }

    const table = useMaterialReactTable({
        columns,
        data: data || [],
        localization: MRT_Localization_PT_BR,

        // Actions Column
        enableRowActions: tableType !== 'createOnly',
        positionActionsColumn: 'last',
        renderRowActions: renderRowActions,

        // Render Functions
        renderTopToolbarCustomActions: renderTopToolbarCustomActions,

        // Mui Style Props
        muiSkeletonProps: {
            animation: 'pulse'
        },
        muiCircularProgressProps: {
            size: 40
        },
        muiToolbarAlertBannerProps: {
            color: 'error',
            variant: 'filled',
            children: 'Não foi possível listar os dados da tabela!',
            sx: {
                marginTop: 2,
                marginBottom: 2,
                borderRadius: 1
            }
        },
        muiTablePaperProps: ({ table }) => ({
            elevation: 0,
            sx: {
                padding: 3
            },
            style: {
                zIndex: table.getState().isFullScreen ? 1300 : undefined,
            }
        }),
        mrtTheme: {
            baseBackgroundColor: "#FFFFFF"
        },

        // State
        state: {
            isLoading,
            isSaving,
            showAlertBanner
        }

    })

    return (
        <MaterialReactTable table={table} />
    )

}

DataTable.propTypes = {
    resource: PropTypes.string.isRequired,
    gender: PropTypes.oneOf(['fem', 'masc']).isRequired,
    tableType: PropTypes.oneOf(['complete', 'createOnly', 'switch', 'editAndSwitch']).isRequired,
    columns: PropTypes.array.isRequired,
    data: PropTypes.array,
    onCreate: PropTypes.func,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    onSwitch: PropTypes.func,
    isLoading: PropTypes.bool.isRequired,
    isSaving: PropTypes.bool.isRequired,
    showAlertBanner: PropTypes.object,
    showButton: PropTypes.bool
}

export default DataTable